import { Editor } from "@tiptap/core";
import a from "indefinite";
import { defer } from "lodash";
import compact from "lodash/compact";
import { ArtifactType } from "types/graphql-schema";

import { ExplorerBookmarkLinks } from "@apps/explorer/components/bookmark-popover";
import { getExplorerBookmarks } from "@apps/explorer/components/bookmark-popover-helpers";
import {
  explorerGithubType,
  explorerHubspotType,
  explorerJiraType,
  getExplorerFiltersUrl,
} from "@apps/explorer/helpers";
import { getLabel } from "@apps/use-label/use-label";
import { currentOrganizationVar, currentUserVar } from "@cache/cache";

import { getTipTapCreatedByUser } from "../helpers";
import {
  asanaCommandItem,
  clickupCommandItem,
  embedUrlCommandItem,
  explorerCommandItem,
  figmaCommandItem,
  findActionItemsCommandItem,
  findDecisionCommandItem,
  findDocumentCommandItem,
  findGithubIssueCommandItem,
  findGoalsCommandItem,
  findHubspotDealCommandItem,
  findJiraIssueCommandItem,
  findRecognitionCommandItem,
  giphyCommandItem,
  githubCommandItem,
  hubspotCommandItem,
  iframeCommandItem,
  jiraCommandItem,
  loomCommandItem,
  miroCommandItem,
  salesforceCommandItem,
  searchCommandItem,
  vimeoCommandItem,
  youtubeCommandItem,
} from "./commands-helpers";

const focusDeleteRange = (editor: Editor, range: any) =>
  editor.chain().focus().deleteRange(range);

export const getSuggestionItems =
  ({
    permissionDecision,
    permissionGoal,
  }: {
    permissionDecision: boolean;
    permissionGoal: boolean;
  }) =>
  ({ query }: { query: string }) => {
    const l = getLabel();
    const currentOrganization = currentOrganizationVar();
    const currentUser = currentUserVar();

    // Create explorer command with logic
    const explorerCommandWithLogic = {
      ...explorerCommandItem,
      command: ({ editor, range }: { editor: Editor; range: any }) => {
        focusDeleteRange(editor, range)
          .addEmptyExplorer({
            creatorName: currentUser.name,
            creatorId: currentUser.id,
          })
          .run();
      },
    };

    // Create find commands with logic and labels
    const findGoalsCommandWithLogic = {
      ...findGoalsCommandItem,
      title: `Find ${a(l("goal"))}`,
      searchFilters: { type: ArtifactType.Goal },
      command: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().run();
      },
    };

    const findRecognitionCommandWithLogic = {
      ...findRecognitionCommandItem,
      title: `Find ${a(l("recognition"))}`,
      searchFilters: { type: ArtifactType.Recognition },
      command: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().run();
      },
    };

    const findDocumentCommandWithLogic = {
      ...findDocumentCommandItem,
      title: `Find ${a("document")}`,
      searchFilters: { type: ArtifactType.Document },
      command: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().run();
      },
    };

    const findDecisionCommandWithLogic = {
      ...findDecisionCommandItem,
      title: `Find ${a("decision")}`,
      searchFilters: { type: ArtifactType.Decision },
      command: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().run();
      },
    };

    const findActionItemsCommandWithLogic = {
      ...findActionItemsCommandItem,
      searchFilters: { type: ArtifactType.ActionItem },
      command: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().run();
      },
    };

    const findHubspotDealCommandWithLogic = {
      ...findHubspotDealCommandItem,
      searchFilters: { type: explorerHubspotType },
      command: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().run();
      },
    };

    const findJiraIssueCommandWithLogic = {
      ...findJiraIssueCommandItem,
      searchFilters: { type: explorerJiraType },
      command: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().run();
      },
    };

    const findGithubIssueCommandWithLogic = {
      ...findGithubIssueCommandItem,
      searchFilters: { type: explorerGithubType },
      command: ({ editor }: { editor: Editor }) => {
        editor.chain().focus().run();
      },
    };

    const explorerBookmarkCommands = compact(getExplorerBookmarks())
      .reduce(
        (memo, group) => [...memo, ...group.links],
        [] as ExplorerBookmarkLinks,
      )
      .filter(({ showInSlashCommand }) => showInSlashCommand)
      .map((explorerLink) => ({
        id: explorerLink.title,
        title: explorerLink.title,
        command: ({ editor, range }: { editor: Editor; range: any }) => {
          focusDeleteRange(editor, range)
            .addEmptyExplorer({
              title: explorerLink.title,
              href: getExplorerFiltersUrl(explorerLink.filters),
              creatorName: currentUser.name,
              creatorId: currentUser.id,
            })
            .run();
        },
      }));
    const commands = compact([
      {
        id: "create-headline",
        title: "Create",
        subItems: compact([
          currentOrganization.featureFlags.actionItems && {
            id: "action-item",
            title: "New action item",
            command: ({ editor, range }: { editor: Editor; range: any }) => {
              // we focus on editor first, so it'll focus automatically on the artifact input when added.
              editor.chain().focus().run();
              defer(() => {
                focusDeleteRange(editor, range)
                  .insertActionItem({
                    createdByUser: getTipTapCreatedByUser(currentUser),
                    title: "",
                  })
                  .focus()
                  .run();
              });
            },
          },
          currentOrganization.featureFlags.decisions && {
            id: "decision",
            title: `New decision${permissionDecision ? "" : " (upgrade)"}`,
            command: ({ editor, range }: { editor: Editor; range: any }) => {
              if (!permissionDecision) {
                return (document.location.href = "/billing");
              }
              // we focus on editor first, so it'll focus automatically on the artifact input when added.
              editor.chain().focus().run();
              defer(() => {
                focusDeleteRange(editor, range)
                  .insertDecision({
                    createdByUser: getTipTapCreatedByUser(currentUser),
                    title: "",
                  })
                  .run();
              });
            },
          },
          currentOrganization.featureFlags.goals && {
            id: "goal",
            title: `New ${l("goal")}${permissionGoal ? "" : " (upgrade)"}`,
            command: ({ editor, range }: { editor: Editor; range: any }) => {
              if (!permissionGoal) {
                return (document.location.href = "/billing");
              }
              // we focus on editor first, so it'll focus automatically on the artifact input when added.
              editor.chain().focus().run();
              defer(() => {
                focusDeleteRange(editor, range)
                  .insertGoal({
                    createdByUser: getTipTapCreatedByUser(currentUser),
                    title: "",
                  })
                  .run();
              });
            },
          },
          currentOrganization.featureFlags.documents && {
            id: "document",
            title: "New document",
            command: ({ editor, range }: { editor: Editor; range: any }) => {
              // we focus on editor first, so it'll focus automatically on the artifact input when added.
              editor.chain().focus().run();
              defer(() => {
                focusDeleteRange(editor, range)
                  .insertDocument({
                    createdByUser: getTipTapCreatedByUser(currentUser),
                    title: "",
                  })
                  .run();
              });
            },
          },
          currentOrganization.featureFlags.recognitions && {
            id: "recognition",
            title: `New ${l("recognition")}`,
            command: ({ editor, range }: { editor: Editor; range: any }) => {
              focusDeleteRange(editor, range)
                .insertRecognition({
                  createdByUser: getTipTapCreatedByUser(currentUser),
                  title: "",
                })
                .run();
            },
          },
        ]),
      },
      {
        id: "insert-headline",
        title: "Insert",
        subItems: compact([
          currentOrganization.featureFlags.kpis && {
            id: "kpi",
            title: "KPI",
            command: ({ editor, range }: { editor: Editor; range: any }) => {
              focusDeleteRange(editor, range)
                .insertKPIEmbed({
                  createdByUser: getTipTapCreatedByUser(currentUser),
                })
                .run();
            },
          },
          currentOrganization.featureFlags.kpis && {
            id: "kpi-group",
            title: "KPI Group",
            command: ({ editor, range }: { editor: Editor; range: any }) => {
              focusDeleteRange(editor, range)
                .insertKPIGroupEmbed({
                  createdByUser: getTipTapCreatedByUser(currentUser),
                })
                .run();
            },
          },
          {
            id: "button",
            title: "Button",
            command: ({ editor, range }: { editor: Editor; range: any }) => {
              focusDeleteRange(editor, range)
                .addButton({
                  createdByUser: getTipTapCreatedByUser(currentUser),
                })
                .run();
            },
          },
          currentOrganization.featureFlags.explorer && searchCommandItem,
          {
            id: "timer",
            title: "Timer",
            command: ({ editor, range }: { editor: Editor; range: any }) => {
              focusDeleteRange(editor, range).addTimer().run();
            },
          },
        ]),
      },
      currentOrganization.featureFlags.explorer && {
        id: "explorer-headline",
        title: "Explorer",
        subItems: [explorerCommandWithLogic].concat(explorerBookmarkCommands),
      },
      currentOrganization.featureFlags.explorer && {
        id: "find-headline",
        title: "Find",
        subItems: compact([
          currentOrganization.featureFlags.actionItems &&
            findActionItemsCommandWithLogic,
          currentOrganization.featureFlags.goals && findGoalsCommandWithLogic,
          currentOrganization.featureFlags.documents &&
            findDocumentCommandWithLogic,
          currentOrganization.featureFlags.recognitions &&
            findRecognitionCommandWithLogic,
          currentOrganization.featureFlags.decisions &&
            findDecisionCommandWithLogic,
          currentUser.hasHubspotAuth && findHubspotDealCommandWithLogic,
          currentUser.hasJiraAuth && findJiraIssueCommandWithLogic,
          currentUser.hasGithubAuth && findGithubIssueCommandWithLogic,
        ]),
      },
      {
        id: "text-layout-headline",
        title: "Text & layout",
        subItems: [
          {
            id: "table",
            title: "Table",
            command: ({ editor, range }: { editor: Editor; range: any }) => {
              focusDeleteRange(editor, range).insertTable().run();
            },
          },
          {
            id: "h1",
            title: "H1",
            command: ({ editor, range }: { editor: Editor; range: any }) => {
              focusDeleteRange(editor, range)
                .setNode("heading", { level: 1 })
                .run();
            },
          },
          {
            id: "h2",
            title: "H2",
            command: ({ editor, range }: { editor: Editor; range: any }) => {
              focusDeleteRange(editor, range)
                .setNode("heading", { level: 2 })
                .run();
            },
          },
          {
            id: "h3",
            title: "H3",
            command: ({ editor, range }: { editor: Editor; range: any }) => {
              focusDeleteRange(editor, range)
                .setNode("heading", { level: 3 })
                .run();
            },
          },
          {
            id: "bullet-list",
            title: "Bullet list",
            command: ({ editor, range }: { editor: Editor; range: any }) => {
              focusDeleteRange(editor, range).toggleBulletList().run();
            },
          },
          {
            id: "ordered-list",
            title: "Ordered list",
            command: ({ editor, range }: { editor: Editor; range: any }) => {
              focusDeleteRange(editor, range).toggleOrderedList().run();
            },
          },
        ],
      },
      {
        id: "integration-headline",
        title: "Integrations",
        subItems: compact([
          embedUrlCommandItem,
          githubCommandItem,
          jiraCommandItem,
          hubspotCommandItem,
          salesforceCommandItem,
          clickupCommandItem,
          asanaCommandItem,
        ]),
      },
      {
        id: "media-headline",
        title: "Media",
        subItems: [
          iframeCommandItem,
          giphyCommandItem,
          figmaCommandItem,
          miroCommandItem,
          loomCommandItem,
          youtubeCommandItem,
          vimeoCommandItem,
        ],
      },
    ]);

    // filter commands and flatten array
    return commands.reduce((memo: any, command: any) => {
      const matchingSubItems = command.subItems.filter(
        ({ title, keywords }: { title: string; keywords?: string }) => {
          const searchableString = title + (keywords || "");
          return searchableString.toLowerCase().includes(query.toLowerCase());
        },
      );
      if (matchingSubItems.length > 0) {
        return [...memo, command, ...matchingSubItems];
      }
      return memo;
    }, []);
  };
